/**
 * AssetDetail page — enriched view with data from TDX, NinjaOne, and Intune.
 * Shows device info, health status, location, assignment, and sync sources.
 */

import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { assetService } from '@/services/assetService';
import { StatusBadge, type BadgeVariant } from '@/components/ui/StatusBadge';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ErrorMessage } from '@/components/ui/ErrorMessage';

const STATUS_VARIANTS: Record<string, BadgeVariant> = {
  'In Use': 'green', Inventory: 'blue', Disposed: 'gray',
  Decommissioned: 'gray', Missing: 'red',
};

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString();
}

export function AssetDetail() {
  const { id } = useParams<{ id: string }>();
  const assetId = Number(id);

  const { data: asset, isLoading, error, refetch } = useQuery({
    queryKey: ['asset', assetId],
    queryFn: () => assetService.getById(assetId),
    enabled: !isNaN(assetId),
  });

  if (isNaN(assetId)) return <ErrorMessage message="Invalid asset ID." />;
  if (isLoading) return <LoadingSpinner message="Loading asset…" />;
  if (error || !asset) return <ErrorMessage message={(error as Error)?.message ?? 'Not found'} onRetry={() => refetch()} />;

  // Cast to access enrichment fields (these come from SQL but aren't in the TS type)
  const a = asset as unknown as Record<string, unknown>;
  const ninjaOnline = a.ninjaOnline ?? a.NinjaOnline;
  const ninjaLastContact = a.ninjaLastContact ?? a.NinjaLastContact;
  const ninjaOSVersion = a.ninjaOSVersion ?? a.NinjaOSVersion;
  const ninjaOrgName = a.ninjaOrgName ?? a.NinjaOrgName;
  const ninjaDeviceId = a.ninjaDeviceId ?? a.NinjaDeviceId;
  const intuneComplianceState = a.intuneComplianceState ?? a.IntuneComplianceState;
  const intuneLastSync = a.intuneLastSync ?? a.IntuneLastSync;
  const intuneOSVersion = a.intuneOSVersion ?? a.IntuneOSVersion;
  const intuneUserPrincipal = a.intuneUserPrincipal ?? a.IntuneUserPrincipal;
  const intuneDeviceId = a.intuneDeviceId ?? a.IntuneDeviceId;
  const lastEnrichmentSync = a.lastEnrichmentSync ?? a.LastEnrichmentSync;

  const hasNinja = ninjaDeviceId != null;
  const hasIntune = intuneDeviceId != null;

  return (
    <div className="max-w-5xl">
      {/* Header */}
      <div className="mb-6">
        <Link to="/assets" className="text-slate-500 no-underline text-sm hover:text-baker-red">← Assets</Link>
        <h1 className="text-lg font-semibold text-text-primary mt-1">
          {asset.deviceName ?? asset.serialNumber}
        </h1>
        <div className="flex gap-2 mt-2 flex-wrap">
          <StatusBadge label={asset.lifecycleStatus} variant={STATUS_VARIANTS[asset.lifecycleStatus] ?? 'gray'} />
          {asset.manufacturer && <StatusBadge label={asset.manufacturer} variant="gray" />}
          {hasNinja && (
            <StatusBadge
              label={ninjaOnline ? 'Ninja: Online' : 'Ninja: Offline'}
              variant={ninjaOnline ? 'green' : 'red'}
            />
          )}
          {hasIntune && (
            <StatusBadge
              label={`Intune: ${String(intuneComplianceState ?? 'unknown')}`}
              variant={intuneComplianceState === 'compliant' ? 'green' : intuneComplianceState === 'noncompliant' ? 'red' : 'gray'}
            />
          )}
        </div>
      </div>

      {/* Device Information */}
      <Section title="Device Information">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 text-sm">
          <Field label="Serial Number" value={asset.serialNumber} mono />
          <Field label="Device Name" value={asset.deviceName} />
          <Field label="Manufacturer" value={asset.manufacturer} />
          <Field label="Model" value={asset.model} />
          <Field label="Device Type" value={asset.deviceType} />
          <Field label="OS" value={asset.os ?? (ninjaOSVersion ? String(ninjaOSVersion) : null) ?? (intuneOSVersion ? String(intuneOSVersion) : null)} />
        </div>
      </Section>

      {/* Location & Assignment */}
      <Section title="Location & Assignment">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 text-sm">
          <div>
            <span className="text-slate-500">Site:</span>{' '}
            {asset.siteNumber ? (
              <Link to={`/sites/${asset.siteNumber}`} className="text-baker-red no-underline hover:underline font-medium">
                {asset.siteNumber}
              </Link>
            ) : '—'}
          </div>
          <Field label="Assigned User" value={asset.assignedUser} />
          {intuneUserPrincipal ? <Field label="Intune User" value={String(intuneUserPrincipal)} /> : null}
          <Field label="Last Logged-In User" value={asset.lastLoggedInUser} />
          <Field label="Last Contact" value={fmtDate(asset.lastContactDate)} />
          {asset.ipAddresses && <Field label="IP Addresses" value={asset.ipAddresses} mono />}
        </div>
      </Section>

      {/* NinjaOne Health */}
      {hasNinja && (
        <Section title="NinjaOne RMM">
          <div className="flex justify-end mb-2">
            <a
              href={`https://us2.ninjarmm.com/#/deviceDashboard/${ninjaDeviceId}/overview`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white text-xs font-medium text-slate-600 no-underline hover:bg-page hover:border-baker-accent/40 transition-all"
            >
              Open in NinjaOne ↗
            </a>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 text-sm">
            <div>
              <span className="text-slate-500">Status:</span>{' '}
              <StatusBadge label={ninjaOnline ? 'Online' : 'Offline'} variant={ninjaOnline ? 'green' : 'red'} />
            </div>
            <Field label="Ninja Device ID" value={String(ninjaDeviceId)} mono />
            {ninjaOrgName ? <Field label="Organization" value={String(ninjaOrgName)} /> : null}
            {ninjaOSVersion ? <Field label="OS Version" value={String(ninjaOSVersion)} /> : null}
            {ninjaLastContact ? <Field label="Last Contact" value={new Date(String(ninjaLastContact)).toLocaleString()} /> : null}
          </div>
        </Section>
      )}

      {/* Intune / Endpoint Manager */}
      {hasIntune && (
        <Section title="Microsoft Intune">
          <div className="flex justify-end mb-2">
            <a
              href={`https://intune.microsoft.com/#/Devices/DeviceInventory/Device/${intuneDeviceId}/Overview`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white text-xs font-medium text-slate-600 no-underline hover:bg-page hover:border-baker-accent/40 transition-all"
            >
              Open in Intune ↗
            </a>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 text-sm">
            <div>
              <span className="text-slate-500">Compliance:</span>{' '}
              <StatusBadge
                label={String(intuneComplianceState ?? 'unknown')}
                variant={intuneComplianceState === 'compliant' ? 'green' : intuneComplianceState === 'noncompliant' ? 'red' : 'gray'}
              />
            </div>
            <Field label="Intune Device ID" value={String(intuneDeviceId)} mono />
            {intuneUserPrincipal ? <Field label="User Principal" value={String(intuneUserPrincipal)} /> : null}
            {intuneOSVersion ? <Field label="OS Version" value={String(intuneOSVersion)} /> : null}
            {intuneLastSync ? <Field label="Last Sync" value={new Date(String(intuneLastSync)).toLocaleString()} /> : null}
          </div>
        </Section>
      )}

      {/* Installed Apps (from Intune) */}
      {hasIntune && (
        <InstalledAppsSectionSafe assetId={assetId} />
      )}

      {/* Compliance Details (from Intune) */}
      {hasIntune && intuneComplianceState === 'noncompliant' && (
        <ComplianceSection assetId={assetId} />
      )}

      {/* SOTI MobiControl (mobile devices) */}
      {(() => {
        const sotiDeviceId = a.sotiDeviceId ?? a.SotiDeviceId;
        const sotiOnline = a.sotiOnline ?? a.SotiOnline;
        const sotiBattery = a.sotiBatteryLevel ?? a.SotiBatteryLevel;
        const sotiCarrier = a.sotiCarrier ?? a.SotiCarrier;
        const sotiOS = a.sotiOSVersion ?? a.SotiOSVersion;
        const sotiLastCheckIn = a.sotiLastCheckIn ?? a.SotiLastCheckIn;
        const sotiPhone = a.sotiPhoneNumber ?? a.SotiPhoneNumber;
        if (!sotiDeviceId) return null;
        return (
          <Section title="SOTI MobiControl">
            <div className="flex justify-end mb-2">
              <a
                href={`https://a0015284.mobicontrol.cloud/MobiControl/WebConsole/home/devices/${sotiDeviceId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-white text-xs font-medium text-slate-600 no-underline hover:bg-page hover:border-baker-accent/40 transition-all"
              >
                Open in SOTI ↗
              </a>
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 text-sm">
              <div>
                <span className="text-slate-500">Status:</span>{' '}
                <StatusBadge label={sotiOnline ? 'Online' : 'Offline'} variant={sotiOnline ? 'green' : 'red'} />
              </div>
              {sotiBattery != null && <Field label="Battery" value={`${String(sotiBattery)}%`} />}
              {sotiCarrier ? <Field label="Carrier" value={String(sotiCarrier)} /> : null}
              {sotiOS ? <Field label="OS Version" value={String(sotiOS)} /> : null}
              {sotiPhone ? <Field label="Phone Number" value={String(sotiPhone)} /> : null}
              {sotiLastCheckIn ? <Field label="Last Check-In" value={new Date(String(sotiLastCheckIn)).toLocaleString()} /> : null}
            </div>
          </Section>
        );
      })()}

      {/* Sync Sources */}
      <Section title="Data Sources">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4 text-sm">
          <div>
            <span className="text-slate-500">NinjaOne:</span>{' '}
            {hasNinja ? <StatusBadge label="Synced" variant="green" /> : <StatusBadge label="Not Found" variant="gray" />}
          </div>
          <div>
            <span className="text-slate-500">Intune:</span>{' '}
            {hasIntune ? <StatusBadge label="Synced" variant="green" /> : <StatusBadge label="Not Found" variant="gray" />}
          </div>
          <div>
            <span className="text-slate-500">TDX:</span>{' '}
            {asset.tdxAssetId ? <StatusBadge label="Linked" variant="green" /> : <StatusBadge label="Not Linked" variant="gray" />}
          </div>
          {lastEnrichmentSync ? <Field label="Last Enrichment" value={new Date(String(lastEnrichmentSync)).toLocaleString()} /> : null}
        </div>
      </Section>

      {/* Notes */}
      {asset.notes && (
        <Section title="Notes">
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{asset.notes}</p>
        </Section>
      )}

      <p className="text-xs text-slate-400 mt-4">
        Created: {fmtDate(asset.createdAt)} · Updated: {fmtDate(asset.updatedAt)}
      </p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-6 bg-white rounded-lg border border-border p-5">
      <h2 className="text-base font-semibold text-text-primary mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Field({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  return (
    <div>
      <span className="text-slate-500">{label}:</span>{' '}
      {value ? (
        mono ? <span className="font-mono text-xs bg-page px-1.5 py-0.5 rounded">{value}</span> : <span>{value}</span>
      ) : '—'}
    </div>
  );
}

function InstalledAppsSectionSafe({ assetId }: { assetId: number }) {
  return <InstalledAppsSection assetId={assetId} />;
}

function InstalledAppsSection({ assetId }: { assetId: number }) {
  const [filter, setFilter] = useState('');
  const { data, isLoading, error } = useQuery({
    queryKey: ['asset-apps', assetId],
    queryFn: async () => {
      try {
        const res = await fetch(`/api/assets/${assetId}/apps`, { credentials: 'include' });
        if (!res.ok) return { apps: [], message: `Failed to load (${res.status})` };
        return res.json();
      } catch {
        return { apps: [], message: 'Network error' };
      }
    },
    retry: false,
  });

  if (isLoading) return <Section title="Installed Applications"><p className="text-sm text-slate-400">Loading apps...</p></Section>;
  if (error) return <Section title="Installed Applications"><p className="text-sm text-slate-400">Error loading apps.</p></Section>;
  if (!data || !data.apps || data.apps.length === 0) {
    return data?.message ? (
      <Section title="Installed Applications"><p className="text-sm text-slate-400">{data.message}</p></Section>
    ) : <Section title="Installed Applications"><p className="text-sm text-slate-400">No apps found.</p></Section>;
  }

  const apps = data.apps as Array<{ displayName: string; version: string; publisher: string; sizeInByte: number }>;
  const filtered = filter
    ? apps.filter((a) => a.displayName.toLowerCase().includes(filter.toLowerCase()) || (a.publisher && a.publisher.toLowerCase().includes(filter.toLowerCase())))
    : apps;

  return (
    <Section title={`Installed Applications (${apps.length})`}>
      <input
        type="text"
        placeholder="Filter apps..."
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="mb-3 w-full max-w-sm py-1.5 px-3 rounded-lg border border-border text-sm focus:outline-none focus:ring-2 focus:ring-baker-accent/40"
      />
      <div className="overflow-x-auto max-h-80 overflow-y-auto">
        <table className="w-full text-[12px] border-collapse">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b-2 border-border">
              <th className="text-left py-1 px-2 font-medium text-slate-600">Application</th>
              <th className="text-left py-1 px-2 font-medium text-slate-600">Version</th>
              <th className="text-left py-1 px-2 font-medium text-slate-600">Publisher</th>
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 200).map((a, i) => (
              <tr key={i} className="border-b border-border-subtle">
                <td className="py-1 px-2">{a.displayName}</td>
                <td className="py-1 px-2 text-slate-500 font-mono text-[11px]">{a.version || '—'}</td>
                <td className="py-1 px-2 text-slate-500">{a.publisher || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {filtered.length > 200 && <p className="text-xs text-slate-400 mt-2">Showing first 200 of {filtered.length} apps.</p>}
    </Section>
  );
}


function ComplianceSection({ assetId }: { assetId: number }) {
  const { data, isLoading } = useQuery({
    queryKey: ['asset-compliance', assetId],
    queryFn: async () => {
      try {
        const res = await fetch(`/api/assets/${assetId}/compliance`, { credentials: 'include' });
        if (!res.ok) return { policies: [], message: 'Failed to load' };
        return res.json();
      } catch { return { policies: [], message: 'Network error' }; }
    },
    retry: false,
  });

  if (isLoading) return <Section title="Compliance Details"><p className="text-sm text-slate-400">Loading...</p></Section>;
  if (!data?.policies?.length) {
    return data?.message ? <Section title="Compliance Details"><p className="text-sm text-slate-400">{data.message}</p></Section> : null;
  }

  const policies = data.policies as Array<{ policyName: string; state: string; settingCount: number; platformType: string }>;

  return (
    <Section title="Compliance Policy Status">
      <div className="overflow-x-auto">
        <table className="w-full text-[13px] border-collapse">
          <thead>
            <tr className="border-b-2 border-border">
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Policy</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">State</th>
              <th className="text-left py-1.5 px-2 font-medium text-slate-600">Settings</th>
            </tr>
          </thead>
          <tbody>
            {policies.map((p, i) => (
              <tr key={i} className="border-b border-border-subtle">
                <td className="py-1.5 px-2">{p.policyName}</td>
                <td className="py-1.5 px-2">
                  <StatusBadge
                    label={p.state === 'compliant' ? 'Compliant' : p.state === 'nonCompliant' ? 'Non-Compliant' : p.state}
                    variant={p.state === 'compliant' ? 'green' : p.state === 'nonCompliant' ? 'red' : 'gray'}
                  />
                </td>
                <td className="py-1.5 px-2 text-slate-500">{p.settingCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Section>
  );
}
